import { createPortal } from 'react-dom'

import { ChangelogEntry } from '../../changelog/changelog.ts'
import {
  shouldHighlightChangelog,
  toMs
} from '../../utils/changelog/handlers.ts'
import { convertDate } from '../../utils/dates.ts'

type ChangelogModalProps = {
  open: boolean
  onClose: () => void
  entries: ChangelogEntry[]
  newestPublishedAtMs: number
}

export function ChangelogModal({
  open,
  onClose,
  entries,
  newestPublishedAtMs
}: ChangelogModalProps) {
  if (!open) return null

  const highlightNewest = shouldHighlightChangelog(newestPublishedAtMs)

  const newestEntryId =
    newestPublishedAtMs > 0
      ? entries.find((e) => toMs(e.publishedAt) === newestPublishedAtMs)?.id
      : undefined

  const modal = (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Co nowego"
      className="fixed inset-0 z-[99999] flex items-start justify-center bg-black/60 p-4 pt-16"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose()
      }}
    >
      <div className="w-full max-w-2xl rounded bg-white p-4">
        <div className="flex items-center justify-between gap-3">
          <div>
            <h2 className="text-lg font-semibold">Co nowego?</h2>
            {newestPublishedAtMs > 0 && (
              <div className="text-sm opacity-70">
                Ostatnia zmiana: {convertDate(new Date(newestPublishedAtMs))}
              </div>
            )}
          </div>

          <button onClick={onClose} className="px-3 py-1">
            Zamknij
          </button>
        </div>

        <div className="mt-4 max-h-[70vh] overflow-auto">
          {entries.length === 0 ? (
            <div>Brak wpisów.</div>
          ) : (
            entries.map((e) => {
              const isNewest = newestEntryId != null && e.id === newestEntryId
              const isHighlighted = highlightNewest && isNewest

              return (
                <section
                  key={e.id}
                  className={
                    'mb-4 rounded border p-3 ' +
                    (isHighlighted
                      ? 'border-amber-300 bg-amber-50'
                      : 'border-gray-200')
                  }
                >
                  <div className="flex items-baseline justify-between gap-3">
                    <div className="flex items-center gap-2 font-semibold">
                      <span>{e.title}</span>
                      {isHighlighted && (
                        <span className="rounded-full bg-amber-600 px-2 py-0.5 text-xs font-medium text-white">
                          OSTATNIA ZMIANA
                        </span>
                      )}
                    </div>

                    <div className="text-sm opacity-70">
                      {convertDate(new Date(e.publishedAt))}
                    </div>
                  </div>

                  <ul className="mt-2 list-disc pl-5">
                    {e.items.map((it) => (
                      <li key={it}>{it}</li>
                    ))}
                  </ul>
                </section>
              )
            })
          )}
        </div>
      </div>
    </div>
  )

  return createPortal(modal, document.body)
}
